import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import {
  faCircleXmark,
  faClone,
  faEyeSlash,
  faPen,
  faWandMagicSparkles,
} from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import type { PropsWithChildren, ReactNode } from 'react';
import { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { DotsLoader } from '@sorare/core/src/atoms/loader/DotsLoader';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { LinkOther } from '@sorare/core/src/atoms/navigation/Box';
import { BodyS } from '@sorare/core/src/atoms/typography';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import { fantasy, glossary } from '@sorare/core/src/lib/glossary';
import { leaderboardHasEntryFees } from '@sorare/core/src/lib/so5';

import { LeaderboardComposable } from 'components/leaderboard/LeaderboardComposable';
import { useDeleteLineup } from 'hooks/so5/useDeleteLineup';
import { useLoadPreviousLineup } from 'hooks/so5/useLoadPreviousLineup';
import { HideTeamDialog } from 'layouts/LineupLayout/HideTeamDialog';
import { getComposeTeamRoute, isFixtureOpened } from 'lib/so5';

import { useLoadSuggestLineup } from '../../../hooks/so5/useLoadSuggestLineup';
import { LazyWarningDialog } from '../LazyWarningDialog';
import type {
  LeaderboardComposableQuery,
  LeaderboardComposableQueryVariables,
  LineupButtons_managerTeam,
  LineupButtons_so5Leaderboard,
  LineupButtons_so5Lineup,
} from './__generated__/index.graphql';

const Root = styled(Horizontal).attrs({ gap: 0 })`
  min-height: calc(calc(6 * var(--unit)));
  padding: var(--half-unit);

  > * {
    flex: 1;
  }

  &.standalone {
    border-radius: var(--unit);
    background-color: var(--c-nd-50-opaque);
  }
`;

const StyledButton = styled(Button)`
  height: calc(5 * var(--unit));
  padding: var(--unit);
  display: flex;
  justify-content: center;
  align-items: center;

  &:first-child {
    border-bottom-left-radius: var(--intermediate-unit);
  }
  &:last-child {
    border-bottom-right-radius: var(--intermediate-unit);
  }

  ${Root}.standalone & {
    border-radius: var(--half-unit);
  }
`;

const ButtonWithDialog = ({
  children,
  dialog,
}: PropsWithChildren<{
  dialog: ({ onClose }: { onClose: () => void }) => ReactNode;
}>) => {
  const [openDialog, setOpenDialog] = useState(false);
  return (
    <>
      <LinkOther
        size="medium"
        as={StyledButton}
        color="transparent"
        onClick={() => setOpenDialog(true)}
      >
        {children}
      </LinkOther>
      {!!openDialog && dialog({ onClose: () => setOpenDialog(false) })}
    </>
  );
};

const LEADERBOARD_COMPOSABLE_QUERY = gql`
  query LeaderboardComposableQuery($slug: String!) {
    so5 {
      so5Leaderboard(slug: $slug) {
        slug
        ...LeaderboardComposable_so5Leaderboard
      }
    }
  }
  ${LeaderboardComposable.fragments.so5Leaderboard}
` as TypedDocumentNode<
  LeaderboardComposableQuery,
  LeaderboardComposableQueryVariables
>;

type Props = {
  so5Lineup?: Nullable<LineupButtons_so5Lineup>;
  managerTeam?: Nullable<LineupButtons_managerTeam>;
  so5Leaderboard: LineupButtons_so5Leaderboard;
  onDelete?: () => void;
  standalone?: boolean;
  hideCreateOrEditButton?: boolean;
};

export const LineupButtons = ({
  so5Lineup,
  managerTeam,
  so5Leaderboard,
  standalone,
  onDelete,
  hideCreateOrEditButton,
}: Props) => {
  const { currentUser } = useCurrentUserContext();
  const { sport } = useSportContext();
  const { formatMessage } = useIntl();
  const deleteLineup = useDeleteLineup();
  const track = useEvents();

  const {
    flags: { useSuggestLineup = false },
  } = useFeatureFlags();

  const isUpcoming =
    so5Leaderboard.so5Fixture && isFixtureOpened(so5Leaderboard.so5Fixture);
  const { value: canCompose } = so5Leaderboard.canCompose || {};

  const [loadPreviousLineup, { loading: loadingLoadPreviousLineup }] =
    useLoadPreviousLineup();
  const [loadSuggestLineup, { loading: loadingLoadSuggestLineup }] =
    useLoadSuggestLineup();

  const mutationLoading = loadingLoadPreviousLineup || loadingLoadSuggestLineup;

  const composeOrEditPath = getComposeTeamRoute({
    leaderboardSlug: so5Leaderboard.slug,
    lineupId: so5Lineup?.id,
    managerTeamId: managerTeam?.id,
    sport,
    eventType: so5Leaderboard.so5Fixture.type.toLowerCase(),
  });

  const displayNotComposableMessage =
    currentUser && isUpcoming && !canCompose && so5Leaderboard;

  const { data, loading } = useQuery(LEADERBOARD_COMPOSABLE_QUERY, {
    variables: { slug: so5Leaderboard.slug },
    skip: !displayNotComposableMessage || !so5Leaderboard,
  });

  const [warningDialogOpen, setWarningDialogOpen] = useState(false);

  const onClickLoadSmartLineup = useCallback(() => {
    if (!managerTeam) {
      return;
    }

    const commonVariables = {
      so5LeaderboardId: so5Leaderboard.id,
      managerTeamId: managerTeam.id,
    };

    if (useSuggestLineup) {
      loadSuggestLineup({
        variables: {
          input: commonVariables,
        },
      });
    } else {
      loadPreviousLineup({
        variables: {
          input: {
            ...commonVariables,
            previousSo5LineupId: managerTeam.lastPreviousSo5Lineup?.id || '',
          },
          so5LeaderboardSlug: so5Leaderboard.slug,
        },
      });
    }
  }, [
    managerTeam,
    so5Leaderboard,
    useSuggestLineup,
    loadSuggestLineup,
    loadPreviousLineup,
  ]);

  if (isUpcoming && !canCompose && displayNotComposableMessage) {
    return (
      <Root className={classNames({ standalone })}>
        {loading || !data ? (
          <LoadingIndicator small />
        ) : (
          <BodyS as="p" color="var(--c-nd-400)">
            <LeaderboardComposable so5Leaderboard={data.so5.so5Leaderboard} />
          </BodyS>
        )}
      </Root>
    );
  }

  if (!canCompose) {
    return null;
  }

  const hasEntryFees = leaderboardHasEntryFees(so5Leaderboard);

  return (
    <Root className={classNames({ standalone })}>
      {so5Lineup ? (
        <>
          {!hideCreateOrEditButton && !hasEntryFees && (
            <LinkOther
              as={StyledButton}
              size="medium"
              aria-label={formatMessage(glossary.delete)}
              color="transparent"
              onClick={async () => {
                await deleteLineup(so5Lineup.id);
                onDelete?.();
              }}
            >
              <Horizontal>
                <FontAwesomeIcon icon={faCircleXmark} />
                <BodyS as="p" bold>
                  <FormattedMessage
                    id="lineup.LineupsButtons.clearTeam"
                    defaultMessage="Clear Team"
                  />
                </BodyS>
              </Horizontal>
            </LinkOther>
          )}
          {!hideCreateOrEditButton && (
            <LinkOther
              size="medium"
              as={StyledButton}
              aria-label={formatMessage(glossary.edit)}
              color="transparent"
              disabled={!so5Leaderboard.slug}
              to={so5Leaderboard.slug ? composeOrEditPath : ''}
            >
              <Horizontal>
                <FontAwesomeIcon icon={faPen} />
                <BodyS as="p" bold>
                  <FormattedMessage {...fantasy.editLineup} />
                </BodyS>
              </Horizontal>
            </LinkOther>
          )}
        </>
      ) : (
        <>
          {!!managerTeam && (
            <>
              {(useSuggestLineup
                ? so5Leaderboard.eligibleToSuggestedLineupLoader
                : so5Leaderboard.eligibleToPreviousLineupLoader) &&
              (managerTeam.lastPreviousSo5Lineup || useSuggestLineup) ? (
                <>
                  <LinkOther
                    size="medium"
                    as={StyledButton}
                    disableDebounce={false}
                    color="transparent"
                    disabled={mutationLoading || !so5Leaderboard.slug}
                    onClick={() => {
                      if (useSuggestLineup) {
                        onClickLoadSmartLineup();
                      } else {
                        setWarningDialogOpen(true);
                      }
                    }}
                  >
                    {mutationLoading ? (
                      <DotsLoader />
                    ) : (
                      <Horizontal>
                        <FontAwesomeIcon
                          icon={
                            useSuggestLineup ? faWandMagicSparkles : faClone
                          }
                        />
                        <BodyS as="p" bold>
                          {useSuggestLineup ? (
                            <FormattedMessage {...fantasy.suggestLineup} />
                          ) : (
                            <FormattedMessage
                              id="lineup.LineupButtons.useLastLineup"
                              defaultMessage="Use Last Lineup"
                            />
                          )}
                        </BodyS>
                      </Horizontal>
                    )}
                  </LinkOther>
                  {!!managerTeam.lastPreviousSo5Lineup &&
                    !!warningDialogOpen && (
                      <LazyWarningDialog
                        open
                        onCancel={() => setWarningDialogOpen(false)}
                        onContinue={() => {
                          setWarningDialogOpen(false);
                          onClickLoadSmartLineup();
                        }}
                        cardsSlugs={managerTeam.lastPreviousSo5Lineup.so5Appearances
                          .map(({ anyCard }) => anyCard?.slug)
                          .filter(Boolean)}
                        so5LeaderboardSlug={so5Leaderboard.slug}
                        so5LineupId={managerTeam.lastPreviousSo5Lineup.id}
                      />
                    )}
                </>
              ) : (
                managerTeam.hidable && (
                  <ButtonWithDialog
                    dialog={({ onClose }) => (
                      <HideTeamDialog
                        onClose={onClose}
                        so5Leaderboard={so5Leaderboard}
                        managerTeam={managerTeam}
                      />
                    )}
                  >
                    <Horizontal>
                      <FontAwesomeIcon icon={faEyeSlash} />
                      <BodyS as="p" bold>
                        <FormattedMessage {...fantasy.hideTeam} />
                      </BodyS>
                    </Horizontal>
                  </ButtonWithDialog>
                )
              )}
            </>
          )}
          {!hideCreateOrEditButton && (
            <LinkOther
              as={StyledButton}
              size="medium"
              color="transparent"
              disabled={!so5Leaderboard.slug}
              to={composeOrEditPath}
              onClick={() => {
                track('Click Set Lineup', {
                  so5LeaderboardSlug: so5Leaderboard.slug,
                  trigger: 'button',
                });
              }}
            >
              <Horizontal>
                <FontAwesomeIcon icon={faPen} />
                <BodyS as="p" bold>
                  <FormattedMessage {...fantasy.setLineup} />
                </BodyS>
              </Horizontal>
            </LinkOther>
          )}
        </>
      )}
    </Root>
  );
};

LineupButtons.fragments = {
  so5Lineup: gql`
    fragment LineupButtons_so5Lineup on So5Lineup {
      id
    }
  ` as TypedDocumentNode<LineupButtons_so5Lineup>,
  managerTeam: gql`
    fragment LineupButtons_managerTeam on ManagerTeam {
      id
      hidable
      lastPreviousSo5Lineup {
        id
        so5Appearances {
          id
          anyCard {
            slug
          }
        }
      }
      ...HideTeamDialog_managerTeam
    }
    ${HideTeamDialog.fragments.managerTeam}
  ` as TypedDocumentNode<LineupButtons_managerTeam>,
  so5Leaderboard: gql`
    fragment LineupButtons_so5Leaderboard on So5Leaderboard {
      slug
      id
      so5Fixture {
        slug
        aasmState
        type
      }
      canCompose {
        value
      }
      eligibleToPreviousLineupLoader
      eligibleToSuggestedLineupLoader
      ...leaderboardHasEntryFees_so5Leaderboard
      ...HideTeamDialog_so5Leaderboard
    }
    ${leaderboardHasEntryFees.fragments.so5Leaderboard}
    ${HideTeamDialog.fragments.so5Leaderboard}
  ` as TypedDocumentNode<LineupButtons_so5Leaderboard>,
};
